import React from 'react';
import { ListItem, ListItemText, Chip, IconButton } from '@mui/material';
import { formatAddress } from '../../../../../helpers/geo';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../../locales/translations';
import { AwarenessCampaignStatus } from '../../../../../models/AwarenessCampaign';
import { useAppSelector } from '../../../../../store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from '../../../../../constants/routes';
import { selectCampaignAddressById } from '../../../../../store/reducers/awareness_campaigns/addresses';
import { EntityId } from '@reduxjs/toolkit';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import AddressItemActions from './AddressItemActions';

interface CampaignAddressItemProps {
    ID: EntityId;
}

const CampaignAddressListItem: React.FC<CampaignAddressItemProps> = ({ ID }) => {
    const { t } = useTranslation([Namespace.GLOSSARY]);

    const navigate = useNavigate();

    const { missionID } = useParams<{ missionID: string }>();

    const missionCompleted = useAppSelector((state) => state.awareness_campaign.selected.selectedAwarenessCampaign?.status === AwarenessCampaignStatus.COMPLETED);
    const addressData = useAppSelector(state => selectCampaignAddressById(state, ID))!;

    const handleAddressDetailClick = () => {
        if (missionID) {
            navigate(
                `/${AppRoutes.AWARENESS_CAMPAIGNS}/${missionID}/${AppRoutes.BATCH_DETAIL}/${addressData.addressKey}`
            );
        }
    };

    const errorsCountLabel = t('errors_count', {
        count: addressData.errorsCount,
        ns: Namespace.GLOSSARY,
    });
    const imagesCountLabel = t('images_count', {
        count: addressData.batchesCount ?? 0,
        ns: Namespace.GLOSSARY,
    });

    return (
        <>
            <ListItem
                onClick={handleAddressDetailClick}
                sx={{
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer',
                    },
                }}
                secondaryAction={
                    <IconButton>
                        <InfoRoundedIcon color="secondary" />
                    </IconButton>
                }
            >
                <ListItemText
                    secondaryTypographyProps={{
                        fontWeight: 900,
                        fontSize: '1rem',
                    }}
                    primary={formatAddress(addressData.address)}
                    secondary={addressData.fromExternalSource ? ( // not from Camia
                        <Chip
                            component={'span'}
                            label={t(
                                addressData.refusalReason ?
                                    'refused_collection' : // added from imported refusals in dashboard
                                    'added_manually', // added from report in ambassador app
                                { ns: Namespace.ADDRESSES, }
                            )}
                            sx={{ color: "rgba(0,0,0,0.6)", mt: 1, alignSelf: 'flex-start' }}
                        />
                    ) : ( // default number of errors and images
                        `${errorsCountLabel} / ${imagesCountLabel}`
                    )}
                />

            </ListItem>

            {!missionCompleted && (
                <AddressItemActions addressID={ID} />
            )}
        </>
    );
};

export default CampaignAddressListItem;
